import { useEffect, useRef, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import * as signalR from '@microsoft/signalr';
import { hubConnectionOptions } from '../utils/hubConnectionOptions';
import { TranslationInfo, TranslationResponse } from '../types/auth';
import { api } from '../services/api';
import { storage } from '../utils/storage';

const BASE_URL = process.env.EXPO_PUBLIC_API_URL || 'https://api.agapella.io';
const PREPARING_SECONDS = 60;
const POLL_INTERVAL_MS = 5000;
const SAMPLE_RATE = 24000;

/** Load a URL into an audio element, using HLS.js for .m3u8 streams */
export function loadAudioSource(audio: HTMLAudioElement, url: string): { destroy: () => void } {
  const isHls = url.endsWith('.m3u8');
  // CORE-451 — `=== 'probably'`, not truthiness: Chrome and Firefox answer
  // 'maybe' for the HLS mime type without being able to play it, so the truthy
  // form skipped hls.js in exactly the browsers that need it.
  const playsHlsNatively = audio.canPlayType('application/vnd.apple.mpegurl') === 'probably';
  if (isHls && !playsHlsNatively) {
    let hls: any = null;
    import('hls.js').then(({ default: Hls }) => {
      if (!Hls.isSupported()) {
        audio.src = url;
        return;
      }
      hls = new Hls({ enableWorker: true });
      hls.loadSource(url);
      hls.attachMedia(audio);
    });
    return { destroy: () => { if (hls) hls.destroy(); } };
  }
  audio.src = url;
  return { destroy: () => {} };
}

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

export interface UseTranslationReturn {
  translations: TranslationInfo[];
  selectedLanguage: string | null;
  preparingSecondsLeft: number | null;
  isPlaying: boolean;
  selectLanguage: (lang: string | null, currentVideoTime?: number, videoPlaying?: boolean) => void;
  requestTranslation: (lang: string) => Promise<void>;
  play: () => void;
  pause: () => void;
  seekTo: (seconds: number) => void;
  setVolume: (volume: number) => void;
  volume: number;
  connectionState: ConnectionState;
  error: string | null;
  // Streaming playback (in-progress translation)
  isStreamingAvailable: boolean;
  isStreamingPlaying: boolean;
  streamingPlay: () => void;
  streamingPause: () => void;
}

export function useTranslation(
  videoId: number,
  initialTranslations: TranslationInfo[],
  authToken: string | null,
): UseTranslationReturn {
  const [translations, setTranslations] = useState<TranslationInfo[]>(initialTranslations);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [preparingSecondsLeft, setPreparingSecondsLeft] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [error, setError] = useState<string | null>(null);

  // Streaming state
  const [isStreamingAvailable, setIsStreamingAvailable] = useState(false);
  const [isStreamingPlaying, setIsStreamingPlaying] = useState(false);
  const translationVolumeRef = useRef(Math.max(storage.getVolume(), 0.1));
  const [volume, setVolumeState] = useState(translationVolumeRef.current);

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingAudioRef = useRef<HTMLAudioElement | null>(null);
   
  const audioHlsRef = useRef<any>(null);
   
  const pendingHlsRef = useRef<any>(null);
  const preparingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Streaming audio refs (Web Audio API)
  const streamingContextRef = useRef<AudioContext | null>(null);
  const nextPlayTimeRef = useRef<number>(0);
  const gainNodeRef = useRef<GainNode | null>(null);
  const chunkBufferRef = useRef<string[]>([]);
  const isStreamingPlayingRef = useRef(false);

  // Update translations when initial data changes (e.g. re-fetch)
  useEffect(() => {
    setTranslations(initialTranslations);
  }, [initialTranslations]);

  // Restore last selected translation language from storage
  useEffect(() => {
    storage.getTranslationLanguage().then(savedLang => {
      if (savedLang && initialTranslations.some(t => t.language === savedLang)) {
        setSelectedLanguage(savedLang);
      }
    });
  // Only run on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showError = useCallback((msg: string) => {
    setError(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setError(null), 5000);
  }, []);

  // Helper: update a single translation in the array
  const updateTranslation = useCallback((language: string, updates: Partial<TranslationInfo>) => {
    setTranslations(prev =>
      prev.map(t => t.language === language ? { ...t, ...updates } : t),
    );
  }, []);

  // ─── Streaming audio playback (Web Audio API) ───

  const getStreamingContext = useCallback(() => {
    if (Platform.OS !== 'web') return null;
    if (!streamingContextRef.current) {
      streamingContextRef.current = new AudioContext();
      const gain = streamingContextRef.current.createGain();
      gain.gain.value = 0; // Start muted until user hits play
      gain.connect(streamingContextRef.current.destination);
      gainNodeRef.current = gain;
    }
    if (streamingContextRef.current.state === 'suspended') {
      streamingContextRef.current.resume();
    }
    return streamingContextRef.current;
  }, []);

  const scheduleChunk = useCallback((base64Data: string) => {
    const ctx = getStreamingContext();
    if (!ctx || !gainNodeRef.current) return;

    // Decode base64 → Int16 PCM → Float32
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const pcmData = new Int16Array(bytes.buffer);
    const floatData = new Float32Array(pcmData.length);
    for (let i = 0; i < pcmData.length; i++) {
      floatData[i] = pcmData[i] / 32768;
    }

    const audioBuffer = ctx.createBuffer(1, floatData.length, SAMPLE_RATE);
    audioBuffer.getChannelData(0).set(floatData);

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(gainNodeRef.current);

    const now = ctx.currentTime;
    const startTime = Math.max(now, nextPlayTimeRef.current);
    source.start(startTime);
    nextPlayTimeRef.current = startTime + audioBuffer.duration;
  }, [getStreamingContext]);

  const handleAudioChunk = useCallback((base64Data: string) => {
    // Always buffer the chunk
    chunkBufferRef.current.push(base64Data);
    setIsStreamingAvailable(true);

    // If user is playing, schedule it immediately
    if (isStreamingPlayingRef.current) {
      scheduleChunk(base64Data);
    }
  }, [scheduleChunk]);

  const streamingPlay = useCallback(() => {
    if (Platform.OS !== 'web') return;

    const ctx = getStreamingContext();
    if (!ctx || !gainNodeRef.current) return;

    // Unmute
    gainNodeRef.current.gain.value = 1;
    isStreamingPlayingRef.current = true;
    setIsStreamingPlaying(true);

    // Play all buffered chunks that haven't been scheduled yet
    // Reset play time so buffered chunks start from now
    nextPlayTimeRef.current = ctx.currentTime;
    for (const chunk of chunkBufferRef.current) {
      scheduleChunk(chunk);
    }
  }, [getStreamingContext, scheduleChunk]);

  const streamingPause = useCallback(() => {
    if (Platform.OS !== 'web') return;
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = 0;
    }
    isStreamingPlayingRef.current = false;
    setIsStreamingPlaying(false);
  }, []);

  const resetStreaming = useCallback(() => {
    isStreamingPlayingRef.current = false;
    setIsStreamingPlaying(false);
    setIsStreamingAvailable(false);
    chunkBufferRef.current = [];
    nextPlayTimeRef.current = 0;
    if (streamingContextRef.current) {
      streamingContextRef.current.close().catch(() => {});
      streamingContextRef.current = null;
    }
    gainNodeRef.current = null;
  }, []);

  // ─── Preparing phase (after translation completes) ───

  const startPreparingPhase = useCallback((language: string, soundTrackUrl: string, autoPlayAtTime?: number) => {
    // Clear any existing timer
    if (preparingTimerRef.current) {
      clearInterval(preparingTimerRef.current);
    }

    // Stop streaming playback — full file is ready
    resetStreaming();

    setPreparingSecondsLeft(PREPARING_SECONDS);

    // Create/reuse audio element for preloading
    if (Platform.OS === 'web') {
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      const audio = audioRef.current;
      audio.volume = translationVolumeRef.current;
      if (audioHlsRef.current) { audioHlsRef.current.destroy(); audioHlsRef.current = null; }
      audioHlsRef.current = loadAudioSource(audio, soundTrackUrl);

      // When audio is buffered enough, skip remaining countdown
      const onCanPlay = () => {
        if (preparingTimerRef.current) {
          clearInterval(preparingTimerRef.current);
          preparingTimerRef.current = null;
        }
        setPreparingSecondsLeft(null);
        updateTranslation(language, { isAvailable: true });
        // Seek to video position — video sync effect handles play/pause
        if (autoPlayAtTime !== undefined) {
          audio.currentTime = autoPlayAtTime;
        }
        audio.removeEventListener('canplaythrough', onCanPlay);
      };
      audio.addEventListener('canplaythrough', onCanPlay);
    }

    // Countdown timer
    preparingTimerRef.current = setInterval(() => {
      setPreparingSecondsLeft(prev => {
        if (prev === null || prev <= 1) {
          if (preparingTimerRef.current) {
            clearInterval(preparingTimerRef.current);
            preparingTimerRef.current = null;
          }
          updateTranslation(language, { isAvailable: true });
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [updateTranslation, resetStreaming]);

  // ─── SignalR connection ───

  useEffect(() => {
    // Only connect if there's an active (Pending/InProgress) translation
    const hasActive = translations.some(
      t => t.status === 'Pending' || t.status === 'InProgress',
    );
    if (!hasActive) return;

    // Skip if ensureConnectedAndJoin already set up a connection
    if (connectionRef.current &&
        (connectionRef.current.state === signalR.HubConnectionState.Connected ||
         connectionRef.current.state === signalR.HubConnectionState.Connecting)) {
      return;
    }

    let destroyed = false;

    // CORE-1364 — token goes through accessTokenFactory, never the URL.
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${BASE_URL}/hubs/translation`, hubConnectionOptions(authToken))
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    // ─── SignalR event handlers ───

    connection.on('TranslationProgress', (data: {
      videoPracticeId: number;
      language: string;
      progressPercent: number;
    }) => {
      if (destroyed || data.videoPracticeId !== videoId) return;
      updateTranslation(data.language, {
        status: 'InProgress',
        progressPercent: data.progressPercent,
      });
    });

    connection.on('TranslationAudioChunk', (data: {
      practiceId: number;
      language: string;
      audioData: string;
      sequenceNumber: number;
      sampleRate: number;
    }) => {
      if (destroyed || data.practiceId !== videoId) return;
      handleAudioChunk(data.audioData);
    });

    connection.on('TranslationCompleted', (data: {
      videoPracticeId: number;
      language: string;
      soundTrackUrl: string;
    }) => {
      if (destroyed || data.videoPracticeId !== videoId) return;
      updateTranslation(data.language, {
        status: 'Completed',
        soundTrackUrl: data.soundTrackUrl,
        progressPercent: 100,
      });
      // Start preparing phase
      if (selectedLanguage === data.language) {
        startPreparingPhase(data.language, data.soundTrackUrl);
      }
    });

    connection.on('TranslationFailed', (data: {
      videoPracticeId: number;
      language: string;
      errorMessage?: string;
    }) => {
      if (destroyed || data.videoPracticeId !== videoId) return;
      updateTranslation(data.language, {
        status: 'Failed',
        progressPercent: 0,
      });
      resetStreaming();
      showError(data.errorMessage || 'Translation failed');
    });

    // ─── Connection lifecycle ───

    connection.onreconnecting(() => {
      if (!destroyed) {
        setConnectionState('reconnecting');
        startPolling();
      }
    });

    connection.onreconnected(() => {
      if (!destroyed) {
        setConnectionState('connected');
        stopPolling();
        // Rejoin
        const active = translations.find(
          t => t.status === 'Pending' || t.status === 'InProgress',
        );
        if (active) {
          connection.invoke('JoinTranslation', videoId, active.language).catch(() => {});
        }
      }
    });

    connection.onclose(() => {
      if (!destroyed) {
        setConnectionState('disconnected');
        startPolling();
      }
    });

    setConnectionState('connecting');

    connection
      .start()
      .then(() => {
        if (destroyed) { connection.stop(); return; }
        setConnectionState('connected');
        stopPolling();
        // Join for each active translation
        const active = translations.find(
          t => t.status === 'Pending' || t.status === 'InProgress',
        );
        if (active) {
          return connection.invoke('JoinTranslation', videoId, active.language);
        }
      })
      .catch(() => {
        if (!destroyed) {
          setConnectionState('disconnected');
          startPolling();
        }
      });

    return () => {
      destroyed = true;
      stopPolling();
      connection.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [videoId, authToken, translations.some(t => t.status === 'Pending' || t.status === 'InProgress')]);

  // ─── Polling fallback ───

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return; // already polling

    const poll = async () => {
      const active = translations.find(
        t => (t.status === 'Pending' || t.status === 'InProgress') && selectedLanguage === t.language,
      );
      if (!active) {
        stopPolling();
        return;
      }
      try {
        const resp = await api.getVideoTranslation(videoId, active.language);
        if (resp.status === 'Completed' && resp.soundTrackUrl) {
          updateTranslation(active.language, {
            status: 'Completed',
            soundTrackUrl: resp.soundTrackUrl,
            progressPercent: 100,
          });
          startPreparingPhase(active.language, resp.soundTrackUrl);
          stopPolling();
        } else if (resp.status === 'Failed') {
          updateTranslation(active.language, {
            status: 'Failed',
            progressPercent: 0,
          });
          showError(resp.errorMessage || 'Translation failed');
          stopPolling();
        } else {
          updateTranslation(active.language, {
            progressPercent: resp.progressPercent,
            status: resp.status,
          });
        }
      } catch {
        // Silent fail, will retry on next interval
      }
    };

    poll();
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [videoId, translations, selectedLanguage, updateTranslation, startPreparingPhase, showError]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // ─── Audio playback (web only, completed translations) ───

  const play = useCallback(() => {
    if (Platform.OS !== 'web' || !audioRef.current) return;
    audioRef.current.play().catch(() => {});
    setIsPlaying(true);
  }, []);

  const pause = useCallback(() => {
    if (Platform.OS !== 'web' || !audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  }, []);

  const setVolume = useCallback((v: number) => {
    translationVolumeRef.current = v;
    setVolumeState(v);
    if (audioRef.current) {
      audioRef.current.volume = v;
    }
  }, []);

  const seekTo = useCallback((seconds: number) => {
    if (Platform.OS !== 'web' || !audioRef.current) return;
    const drift = Math.abs(audioRef.current.currentTime - seconds);
    if (drift > 0.5) {
      audioRef.current.currentTime = seconds;
    }
  }, []);

  const selectLanguage = useCallback((lang: string | null, currentVideoTime?: number, videoPlaying?: boolean) => {
    // Cancel any in-flight pending audio load
    if (pendingAudioRef.current) {
      pendingAudioRef.current.pause();
      pendingAudioRef.current.src = '';
      pendingAudioRef.current = null;
    }
    if (pendingHlsRef.current) { pendingHlsRef.current.destroy(); pendingHlsRef.current = null; }

    setPreparingSecondsLeft(null);
    if (preparingTimerRef.current) {
      clearInterval(preparingTimerRef.current);
      preparingTimerRef.current = null;
    }

    // Reset streaming state when switching languages
    resetStreaming();

    const oldAudio = audioRef.current;
    const wasPlaying = oldAudio ? !oldAudio.paused : false;

    setSelectedLanguage(lang);
    if (lang) {
      storage.setTranslationLanguage(lang);
    }

    if (lang) {
      const tr = translations.find(t => t.language === lang);
      if (tr?.status === 'Completed' && tr.soundTrackUrl && tr.isAvailable) {
        // Already ready — seamless switch: load new audio before stopping old
        if (Platform.OS === 'web') {
          const newAudio = new Audio();
          newAudio.volume = translationVolumeRef.current;
          pendingAudioRef.current = newAudio;
          const oldPendingHls = pendingHlsRef.current;
          if (oldPendingHls) { oldPendingHls.destroy(); pendingHlsRef.current = null; }
          pendingHlsRef.current = loadAudioSource(newAudio, tr.soundTrackUrl);

          const onCanPlay = () => {
            newAudio.removeEventListener('canplaythrough', onCanPlay);
            // Abort if another switch happened while loading
            if (pendingAudioRef.current !== newAudio) {
              newAudio.pause();
              newAudio.src = '';
              if (pendingHlsRef.current) { pendingHlsRef.current.destroy(); pendingHlsRef.current = null; }
              return;
            }
            pendingAudioRef.current = null;

            // Stop old audio and transfer HLS instance
            if (oldAudio) {
              oldAudio.pause();
            }
            if (audioHlsRef.current) { audioHlsRef.current.destroy(); }
            audioHlsRef.current = pendingHlsRef.current;
            pendingHlsRef.current = null;
            audioRef.current = newAudio;

            if (currentVideoTime !== undefined) {
              newAudio.currentTime = currentVideoTime;
            }
            // Only auto-play if the video is currently playing
            if (videoPlaying) {
              newAudio.play().catch(() => {});
              setIsPlaying(true);
            } else {
              setIsPlaying(false);
            }
          };
          newAudio.addEventListener('canplaythrough', onCanPlay);

          // Keep isPlaying true during transition to avoid UI flicker
          // (old audio keeps playing until new one is ready)
          return;
        }
      } else if (tr?.status === 'Completed' && tr.soundTrackUrl && !tr.isAvailable) {
        // Already completed but not yet prepared — start preparing (auto-play synced if mid-video)
        if (oldAudio) {
          oldAudio.pause();
          oldAudio.currentTime = 0;
        }
        audioRef.current = null;
        setIsPlaying(false);
        startPreparingPhase(lang, tr.soundTrackUrl, currentVideoTime);
        return;
      }
    }

    // Default: switching to original or non-ready translation — stop old audio
    if (oldAudio) {
      oldAudio.pause();
      oldAudio.currentTime = 0;
    }
    audioRef.current = null;
    setIsPlaying(false);
  }, [translations, startPreparingPhase, resetStreaming]);

  const ensureConnectedAndJoin = useCallback(async (lang: string): Promise<void> => {
    // If already connected, just join the room
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      await connectionRef.current.invoke('JoinTranslation', videoId, lang).catch(() => {});
      return;
    }
    // Otherwise connect, register handlers, then join — before the POST
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${BASE_URL}/hubs/translation`, hubConnectionOptions(authToken))
      .withAutomaticReconnect()
      .build();

    // Register event handlers (same as the effect)
    conn.on('TranslationProgress', (data: any) => {
      if (data.videoPracticeId !== videoId) return;
      updateTranslation(data.language, { status: 'InProgress', progressPercent: data.progressPercent });
    });
    conn.on('TranslationAudioChunk', (data: any) => {
      if (data.practiceId !== videoId) return;
      handleAudioChunk(data.audioData);
    });
    conn.on('TranslationCompleted', (data: any) => {
      if (data.videoPracticeId !== videoId) return;
      updateTranslation(data.language, { status: 'Completed', soundTrackUrl: data.soundTrackUrl, progressPercent: 100 });
      if (selectedLanguage === data.language || lang === data.language) {
        startPreparingPhase(data.language, data.soundTrackUrl);
      }
    });
    conn.on('TranslationFailed', (data: any) => {
      if (data.videoPracticeId !== videoId) return;
      updateTranslation(data.language, { status: 'Failed', progressPercent: 0 });
      resetStreaming();
      showError(data.errorMessage || 'Translation failed');
    });

    // Reconnection lifecycle
    conn.onreconnecting(() => {
      setConnectionState('reconnecting');
      startPolling();
    });
    conn.onreconnected(() => {
      setConnectionState('connected');
      stopPolling();
      conn.invoke('JoinTranslation', videoId, lang).catch(() => {});
    });
    conn.onclose(() => {
      setConnectionState('disconnected');
      startPolling();
    });

    connectionRef.current = conn;
    setConnectionState('connecting');
    try {
      await conn.start();
      setConnectionState('connected');
      stopPolling();
      await conn.invoke('JoinTranslation', videoId, lang);
    } catch {
      setConnectionState('disconnected');
      startPolling();
    }
  }, [videoId, authToken, selectedLanguage, updateTranslation, handleAudioChunk, startPreparingPhase, resetStreaming, showError, startPolling, stopPolling]);

  const requestTranslation = useCallback(async (lang: string) => {
    try {
      // Connect to SignalR and join room BEFORE requesting — ensures we
      // receive WS events even for fast translations
      await ensureConnectedAndJoin(lang);

      const resp = await api.requestVideoTranslation(videoId, lang);
      const newTranslation: TranslationInfo = {
        language: lang,
        displayName: lang,
        isAvailable: false,
        status: resp.status,
        soundTrackUrl: resp.soundTrackUrl,
        progressPercent: resp.progressPercent,
      };
      setTranslations(prev => {
        const exists = prev.find(t => t.language === lang);
        if (exists) {
          return prev.map(t => t.language === lang ? { ...t, ...newTranslation } : t);
        }
        return [...prev, newTranslation];
      });
      setSelectedLanguage(lang);

      // If the response already has a soundTrackUrl (instant completion), handle it
      if (resp.status === 'Completed' && resp.soundTrackUrl) {
        startPreparingPhase(lang, resp.soundTrackUrl);
      } else {
        // Start polling as backup in case WS misses events
        startPolling();
      }
    } catch (err: any) {
      showError(err.message || 'Failed to request translation');
    }
  }, [videoId, showError, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioHlsRef.current) { audioHlsRef.current.destroy(); audioHlsRef.current = null; }
      if (pendingAudioRef.current) {
        pendingAudioRef.current.pause();
        pendingAudioRef.current.src = '';
        pendingAudioRef.current = null;
      }
      if (pendingHlsRef.current) { pendingHlsRef.current.destroy(); pendingHlsRef.current = null; }
      if (preparingTimerRef.current) {
        clearInterval(preparingTimerRef.current);
      }
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      stopPolling();
      resetStreaming();
    };
  }, [stopPolling, resetStreaming]);

  return {
    translations,
    selectedLanguage,
    preparingSecondsLeft,
    isPlaying,
    selectLanguage,
    requestTranslation,
    play,
    pause,
    seekTo,
    setVolume,
    volume,
    connectionState,
    error,
    isStreamingAvailable,
    isStreamingPlaying,
    streamingPlay,
    streamingPause,
  };
}
